import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { darkTheme, FONT } from '../../theme/tokens';

const t = darkTheme;

const GAP = 8;
const MAX_W = 300;
const PAD = 8; // keep the panel this far from the viewport edges

/**
 * An inline hover/focus hint — the styled popover panel (same look as the
 * table's FindingTooltip) for a sentence of explanatory copy on a badge, pill,
 * or note. Replaces native `title` tooltips, which are unstyled, slow to
 * appear, and invisible to keyboard users: the trigger here is focusable, the
 * panel shows on hover OR focus, dismisses on Escape, and is announced via
 * `aria-describedby`. Portals to `document.body` with `position: fixed` so it
 * escapes any `overflow` clipping around the trigger.
 */
export function HoverHint({ hint, children }: { hint: string; children: ReactNode }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const id = useId();
  return (
    <span
      onMouseEnter={(e) => setAnchor(e.currentTarget.getBoundingClientRect())}
      onMouseLeave={() => setAnchor(null)}
      onFocus={(e) => setAnchor(e.currentTarget.getBoundingClientRect())}
      onBlur={() => setAnchor(null)}
      onKeyDown={(e) => {
        // Consume Escape ONLY while a panel is open — layered dismissal. The
        // finding overlay / drawer listen for Escape on `document` to close
        // themselves; without the gate + stopPropagation, dismissing just the
        // hint would also close the surface under it (Codex P2). With no panel
        // open, Escape passes through untouched.
        if (e.key === 'Escape' && anchor) {
          e.stopPropagation();
          setAnchor(null);
        }
      }}
      tabIndex={0}
      aria-describedby={anchor ? id : undefined}
      style={{ display: 'inline-flex', maxWidth: '100%' }}
    >
      {children}
      {anchor && (
        <HintPanel id={id} anchor={anchor}>
          {hint}
        </HintPanel>
      )}
    </span>
  );
}

/** The floating panel. Measures itself, clamps to the viewport, and prefers
 *  above the anchor — flipping below when there's more room there. */
function HintPanel({ id, anchor, children }: { id: string; anchor: DOMRect; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const left = Math.max(PAD, Math.min(anchor.left, window.innerWidth - width - PAD));
    const placeAbove = anchor.top - GAP - height >= PAD || anchor.top > window.innerHeight - anchor.bottom;
    const top = placeAbove ? anchor.top - GAP - height : anchor.bottom + GAP;
    setPos({ left, top });
  }, [anchor]);

  return createPortal(
    <div
      ref={ref}
      id={id}
      role="tooltip"
      style={{
        position: 'fixed',
        zIndex: 1200,
        left: pos?.left ?? anchor.left,
        top: pos?.top ?? 0,
        // Hidden on the first (measuring) pass so it never flashes misplaced.
        visibility: pos ? 'visible' : 'hidden',
        maxWidth: MAX_W,
        background: t.surface,
        border: `1px solid ${t.border2}`,
        borderRadius: 9,
        padding: '10px 12px',
        fontSize: 12,
        lineHeight: 1.4,
        color: t.text2,
        fontFamily: FONT,
        boxShadow: '0 8px 24px rgba(0,0,0,.5)',
        pointerEvents: 'none',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
